import React from 'react';
import classNames from 'classnames';

import { Button, TourQuantity } from '@lt/components';
import {
  Hotel as HotelIcon,
  Loader as LoaderIcon,
} from '@lt/components/icons-react/rebrand';

import type { IHotelAirport } from 'checkout/store/hotel/types';
import { useTranslation } from '@lt/localization';
import { pluralizeNights } from 'src/utils/pluralizeUtils';
import { FLIGHT_TYPES_NAME } from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/constants';
import type {
  FlightsData,
  TransformedBackFlight,
} from 'checkout/CheckoutRoutes/PackageCheckout/types/flights';

import { findFlight } from 'checkout/CheckoutRoutes/PackageCheckout/utils/flightUtils';

import FlightSummary from 'src/routes/Checkout/components/PriceSummary/FlightSummary/FlightSummary';
import FlightChangeWarning from '../../FlightChangeWarning';
import FlightsRoute from '../../FlightsRoute';
import LabelsContainer from '../../LabelsContainer';
import FlightsLuggageSummary from '../../Luggage/FlightsLuggageSummary';
import SkeletonDetails from '../../SkeletonDetails/SkeletonDetails';
import { UNKNOWN_LUGGAGE } from '../../constants';
import CheapestFlightLabel from '../../CheapestFlightLabel';

import type { ExtraItem } from '../../../../../../types/extras';

import styles from './flightItem.module.css';
import FlightsLabelList from '../../FlightsLabels';

export const baseCls = 'checkout-flight';
export const detailsCls = `${baseCls}-details`;

export interface FlightsItemProps {
  active?: boolean;
  className: string;
  buttonText?: string;
  data: FlightsData;
  couldBeChanged?: boolean;
  allExtras: ExtraItem[];
  packagePrice: number;
  operatorID: number;
  instantConfirm?: boolean;
  onSelect: (arg: {
    flight_pair_id: TransformedBackFlight['flight_pair_id'];
    allExtras: ExtraItem[];
    tour_id: TransformedBackFlight['tour_id'];
  }) => void;
  selectedBackId: string;
  showFuelChargeSeparator?: boolean;
  isLoading: boolean;
  flightNumberTo: string;
  flightNumberBack: string;
  transferAndMedInsurance: {
    insurance: boolean;
    transfer: boolean;
    airports: IHotelAirport[];
    transferPrivate: boolean;
  };
  hasDirectCheapestLabel: boolean;
  hasCheapestLabel: boolean;
  airlines: [number, string][];
}

const FlightsItem = ({
  data: { to, back, datesInfo },
  allExtras,
  onSelect,
  selectedBackId,
  packagePrice,
  couldBeChanged,
  buttonText,
  showFuelChargeSeparator,
  instantConfirm,
  active,
  isLoading,
  transferAndMedInsurance,
  flightNumberBack,
  flightNumberTo,
  className,
  hasCheapestLabel,
  hasDirectCheapestLabel,
  operatorID,
  airlines,
}: FlightsItemProps) => {
  const { t } = useTranslation();
  const selectedBack = Array.isArray(back)
    ? findFlight(back, selectedBackId) || back[0] // Ищем самый дешевый
    : back;
  const { flight_pair_id, fuel_charge, availability, tour_id } = selectedBack;
  const detailsBlockCls = classNames(`${detailsCls} ${baseCls}__details`);
  const summaryLuggage = couldBeChanged
    ? [UNKNOWN_LUGGAGE, UNKNOWN_LUGGAGE]
    : [to.luggage, selectedBack.luggage];
  const { insurance, transfer, airports, transferPrivate } =
    transferAndMedInsurance;

  const { nights_count: nightsInHotel } = datesInfo;

  return (
    <div className={className}>
      {' '}
      <div
        className={classNames(baseCls, 'checkout-flights__item', {
          [`${baseCls}__active`]: active,
        })}
      >
        {hasCheapestLabel && (
          <CheapestFlightLabel isMobile={false} direct={false} />
        )}
        {hasDirectCheapestLabel && !hasCheapestLabel && (
          <CheapestFlightLabel isMobile={false} direct />
        )}

        <div className={detailsBlockCls}>
          {couldBeChanged ? (
            <>
              <FlightChangeWarning airlines={airlines} />
              <SkeletonDetails
                isMobile={false}
                to={{
                  departure: to.departure,
                  arrival: to.arrival,
                  departureCity: to.origin && to.origin.city.name,
                  arrivalCity: to.destination && to.destination.city.name,
                  flightType: FLIGHT_TYPES_NAME.CHARTER,
                }}
                back={{
                  departure: selectedBack.departure,
                  arrival: selectedBack.arrival,
                  departureCity:
                    selectedBack.origin && selectedBack.origin.city.name,
                  arrivalCity:
                    selectedBack.destination &&
                    selectedBack.destination.city.name,
                  flightType: FLIGHT_TYPES_NAME.CHARTER,
                }}
              />
            </>
          ) : (
            <>
              {' '}
              <div className={`${detailsCls}__item`}>
                <div className={`${detailsCls}__item-label`}>
                  {t('checkout.flight.item.to')}
                </div>
                <FlightsRoute data={to} type="to" showLogo />
                <LabelsContainer
                  isMobile={false}
                  data={to}
                  type="to"
                  flightNumber={flightNumberTo}
                  isBusinessClass={to.business}
                  operatorID={operatorID}
                />
              </div>
              <div className={styles.flightFuelSeparator}>
                <HotelIcon className={styles.hotelIcon} />
                <span className={styles.flightFuelText}>
                  <span>{t('checkout.flight.separator.withTheseFlights')}</span>{' '}
                  <span>{nightsInHotel}</span>{' '}
                  <span>{pluralizeNights(nightsInHotel)}</span>{' '}
                  <span>{t('checkout.flight.separator.inHotel')}</span>{' '}
                </span>
                <div className={styles.flightSeparator} />
              </div>
              <div
                className={classNames(`${detailsCls}__item`, {
                  [`${detailsCls}__item_single`]: showFuelChargeSeparator,
                })}
              >
                <div
                  className={`${detailsCls}__item-label ${detailsCls}__item-label_back`}
                >
                  {t('checkout.flight.item.back')}
                </div>
                <FlightsRoute type="back" data={selectedBack} showLogo />
                <LabelsContainer
                  isMobile={false}
                  type="back"
                  data={selectedBack}
                  flightNumber={flightNumberBack}
                  isBusinessClass={selectedBack.business}
                  operatorID={operatorID}
                />
              </div>
            </>
          )}
        </div>

        <div className={`${baseCls}__details-right`}>
          <FlightsLabelList tourId={tour_id} />
          <FlightsLuggageSummary summaryLuggage={summaryLuggage} />

          <div className={styles.summary}>
            <FlightSummary
              packagePrice={packagePrice}
              fuelCharge={fuel_charge}
              extras={allExtras}
              instantConfirm={instantConfirm}
              tourId={tour_id}
              isMobile={false}
              insurance={insurance}
              transfer={transfer}
              airports={airports}
              transferPrivate={transferPrivate}
            />
            <Button
              className={styles.button}
              primary
              size="none"
              onClick={() => {
                onSelect({ flight_pair_id, allExtras, tour_id });
              }}
              data-at-package-select-flight
            >
              {isLoading ? (
                <LoaderIcon className={styles.loader} />
              ) : (
                buttonText
              )}
            </Button>

            <TourQuantity
              position="center"
              text={availability && availability.tooltip}
              withTooltip={false}
              availability={availability}
              maxWidth="160px"
              isPackageCheckout
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightsItem;
